import React from 'react';
import { RouteErrorBoundary } from '@/components/ErrorBoundary';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/contexts/SupabaseAuthContext';
import { OrgProvider } from '@/contexts/OrgContext';
import BrandTheme from '@/components/BrandTheme';
import AuthStateListener from '@/components/auth/AuthStateListener';
import AppUpdateGate, { UpdateOnNavigate } from '@/components/AppUpdateGate';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import Analytics from '@/components/Analytics';
import ConsentBanner from '@/components/ConsentBanner';
import WhatsAppChatButton from '@/components/WhatsAppChatButton';
import PrerenderedFallback from '@/components/PrerenderedFallback';
import PublicType from '@/components/PublicType';
const SubcontractorLoginPage = React.lazy(() => import('@/pages/auth/SubcontractorLoginPage'));
const DemoEntryPage = React.lazy(() => import('@/pages/demo/DemoEntryPage'));
const SplashLandingPage = React.lazy(() => import('@/pages/SplashLandingPage'));
const ProofPage = React.lazy(() => import('@/pages/ProofPage'));
const AboutPage = React.lazy(() => import('@/pages/AboutPage'));
const SoftwarePage = React.lazy(() => import('@/pages/SoftwarePage'));
const NotFoundPage = React.lazy(() => import('@/pages/NotFoundPage'));
const TenantSignupPage = React.lazy(() => import('@/pages/runmark/TenantSignupPage'));
const TenantWelcomePage = React.lazy(() => import('@/pages/runmark/TenantWelcomePage'));
const RunmarkLandingPage = React.lazy(() => import('@/pages/runmark/RunmarkLandingPage'));
const BuildYourVanPage = React.lazy(() => import('@/pages/runmark/BuildYourVanPage'));
// Part product pages: private by default, demo-gated, public per part when the
// admin switches it on in the console Catalogue (platform_parts.public_enabled).
const PartsIndexPage = React.lazy(() => import('@/pages/runmark/parts/PartsIndexPage'));
const PartPage = React.lazy(() => import('@/pages/runmark/parts/PartPage'));
const ConsoleLayout = React.lazy(() => import('@/pages/runmark/console/ConsoleLayout'));
const ConsoleOverviewPage = React.lazy(() => import('@/pages/runmark/console/OverviewPage'));
const ConsoleTenantsPage = React.lazy(() => import('@/pages/runmark/console/TenantsPage'));
const ConsoleTenantPage = React.lazy(() => import('@/pages/runmark/console/TenantPage'));
const ConsoleCataloguePage = React.lazy(() => import('@/pages/runmark/console/CataloguePage'));
const ConsolePartCopyPage = React.lazy(() => import('@/pages/runmark/console/PartCopyPage'));
const ConsoleBillingPage = React.lazy(() => import('@/pages/runmark/console/BillingPage'));
const ConsoleDomainsPage = React.lazy(() => import('@/pages/runmark/console/DomainsPage'));
const ConsoleAuditPage = React.lazy(() => import('@/pages/runmark/console/AuditPage'));
const ConsoleSettingsPage = React.lazy(() => import('@/pages/runmark/console/SettingsPage'));
const ConsoleAccessPage = React.lazy(() => import('@/pages/runmark/console/AccessPage'));
const ConsoleHealthPage = React.lazy(() => import('@/pages/runmark/console/HealthPage'));
const RunmarkLoginPage = React.lazy(() => import('@/pages/runmark/RunmarkLoginPage'));
import AgreementGate from '@/components/auth/AgreementGate';
import MessageGate from '@/components/subcontractor/MessageGate';
import ArchiveGate from '@/components/subcontractor/ArchiveGate';
import DemoDeckBar from '@/components/subcontractor/DemoDeckBar';
import DemoRouteGuard from '@/components/subcontractor/DemoRouteGuard';
import { DemoAccessProvider } from '@/contexts/DemoAccessContext';
import { FIELD_ASSIST_ENABLED } from '@/lib/featureFlags';
import { unroutedTenantHost, httpsUrl } from '@/lib/tenantHost';
import ModuleGate from '@/components/ModuleGate';
// Field Assist (ported, gated OFF by default via FIELD_ASSIST_ENABLED)
const FieldAssistPage = React.lazy(() => import('@/pages/subcontractor/FieldAssistPage'));
const FieldIntelligenceReviewDashboard = React.lazy(() => import('@/pages/admin/FieldIntelligenceReviewDashboard'));
const ResearchProgrammePage = React.lazy(() => import('@/pages/admin/ResearchProgrammePage'));

const AdminLoginPage = React.lazy(() => import('@/pages/auth/AdminLoginPage'));
const SubcontractorDashboardPage = React.lazy(() => import('@/pages/subcontractor/DashboardPage'));
const AdminDashboardPage = React.lazy(() => import('@/pages/admin/DashboardPage'));
const AdminDepotsPage = React.lazy(() => import('@/pages/admin/DepotsPage'));
// Stages 07-09 of the onboarding line, inside the tenant's own portal.
const AdminSetupPage = React.lazy(() => import('@/pages/admin/SetupPage'));
const AdminCrewKitPage = React.lazy(() => import('@/pages/admin/CrewKitPage'));
const AdminBrandingPage = React.lazy(() => import('@/pages/admin/BrandingPage'));
const AdminPlatformPage = React.lazy(() => import('@/pages/admin/PlatformPage'));
const AdminPartnerPlanPage = React.lazy(() => import('@/pages/admin/PartnerPlanPage'));
const AdminSendingIdentityPage = React.lazy(() => import('@/pages/admin/SendingIdentityPage'));
const AdminWebAddressPage = React.lazy(() => import('@/pages/admin/WebAddressPage'));
const AdminCrewsPage = React.lazy(() => import('@/pages/admin/CrewsPage'));
const AdminSubcontractorsPage = React.lazy(() => import('@/pages/admin/SubcontractorsPage'));
const AdminCalendarsPage = React.lazy(() => import('@/pages/admin/CalendarsPage'));
const AdminWebhooksPage = React.lazy(() => import('@/pages/admin/WebhooksPage'));
const AdminAllocationsPage = React.lazy(() => import('@/pages/admin/AllocationsPage.jsx'));
const AdminLoggingPage = React.lazy(() => import('@/pages/admin/AdminLoggingPage.jsx'));
const ActivityPage = React.lazy(() => import('@/pages/admin/ActivityPage.jsx'));
const AdminDashboardShell = React.lazy(() => import('@/components/AdminDashboardShell'));

const AdminInvoicingPage = React.lazy(() => import('@/pages/admin/AdminInvoicingPage.jsx'));
const PaymentSchedulesPage = React.lazy(() => import('@/pages/admin/PaymentSchedulesPage.jsx'));
const PartnershipsPage = React.lazy(() => import('@/pages/admin/PartnershipsPage.jsx'));
const PayRunPage = React.lazy(() => import('@/pages/admin/PayRunPage.jsx'));
const InvoiceSettingsPage = React.lazy(() => import('@/pages/admin/InvoiceSettingsPage.jsx'));
const AdminSyncLogsPage = React.lazy(() => import('@/pages/admin/AdminSyncLogsPage.jsx'));
const AdminRoutingPage = React.lazy(() => import('@/pages/admin/RoutingPage.jsx'));
const FinancialsPage = React.lazy(() => import('@/pages/admin/FinancialsPage.jsx'));
const AccountingPage = React.lazy(() => import('@/pages/admin/AccountingPage.jsx'));
const AccountingSettingsPage = React.lazy(() => import('@/pages/admin/AccountingSettingsPage.jsx'));
const DirectorRemunerationPage = React.lazy(() => import('@/pages/admin/DirectorRemunerationPage.jsx'));
const UncategorizedTransactionsPage = React.lazy(() => import('@/pages/admin/UncategorizedTransactionsPage.jsx'));
const DataToolsPage = React.lazy(() => import('@/pages/admin/DataToolsPage.jsx'));
const ProductDemosPage = React.lazy(() => import('@/pages/admin/ProductDemosPage.jsx'));
const DecksPage = React.lazy(() => import('@/pages/admin/DecksPage.jsx'));
const DeckViewerPage = React.lazy(() => import('@/pages/DeckViewerPage.jsx'));
const ProjectorStagePage = React.lazy(() => import('@/pages/admin/ProjectorStagePage.jsx'));
const BusinessVanLabPage = React.lazy(() => import('@/pages/admin/BusinessVanLabPage.jsx'));
const TimeRequestsPage = React.lazy(() => import('@/pages/admin/TimeRequestsPage.jsx'));
const AdminUniformsPage = React.lazy(() => import('@/pages/admin/AdminUniformsPage'));
const AdminTerminationsPage = React.lazy(() => import('@/pages/admin/AdminTerminationsPage'));

const AdminMessagesPage = React.lazy(() => import('@/pages/admin/MessagesPage'));
const AdminMailPage = React.lazy(() => import('@/pages/admin/MailPage.jsx'));
const AdminNotificationsPage = React.lazy(() => import('@/pages/admin/NotificationsPage'));
const AdminSettingsPage = React.lazy(() => import('@/pages/admin/SettingsPage'));
const AIInsightsPage = React.lazy(() => import('@/pages/admin/AIInsightsPage'));
const AdminAnalyticsPage = React.lazy(() => import('@/pages/admin/AnalyticsPage'));
const AdminPerformanceHub = React.lazy(() => import('@/pages/admin/AdminPerformanceHub'));
const NetworkScoreboardPage = React.lazy(() => import('@/pages/admin/NetworkScoreboardPage'));
const CrewTrendsPage = React.lazy(() => import('@/pages/admin/CrewTrendsPage'));
const ReportsArchivePage = React.lazy(() => import('@/pages/admin/ReportsArchivePage'));
const AdditionalWorkPage = React.lazy(() => import('@/pages/admin/AdditionalWorkPage'));
const AvailabilityCancellationsPage = React.lazy(() => import('@/pages/admin/AvailabilityCancellationsPage'));
const RecruitmentPage = React.lazy(() => import('@/pages/admin/RecruitmentPage'));
const RecruitmentRedirect = React.lazy(() => import('@/pages/admin/RecruitmentRedirect'));
const MarketingPage = React.lazy(() => import('@/pages/admin/MarketingPage'));
const EngineRoomPage = React.lazy(() => import('@/pages/admin/EngineRoomPage'));
const PlatformHealthPage = React.lazy(() => import('@/pages/admin/PlatformHealthPage'));
const JobArchivePage = React.lazy(() => import('@/pages/admin/JobArchivePage'));
const ManagementDashboard = React.lazy(() => import('@/pages/drivers/ManagementDashboard'));
const LegalPage = React.lazy(() => import('@/pages/drivers/LegalPage'));
const NationalInsightsPage = React.lazy(() => import('@/pages/admin/NationalInsightsPage'));
const AdminAgreementsPage = React.lazy(() => import('@/pages/admin/AgreementsPage'));
const AdminFieldCasesPage = React.lazy(() => import('@/pages/admin/FieldCasesPage'));
const AdminProtocolsPage = React.lazy(() => import('@/pages/admin/ProtocolsPage'));
const AdminReceiptScannerPage = React.lazy(() => import('@/pages/admin/AdminReceiptScannerPage.jsx'));
const SubcontractorInspector = React.lazy(() => import('@/pages/admin/SubcontractorInspector'));
const MagicLinkConfirmationPage = React.lazy(() => import('@/pages/auth/MagicLinkConfirmationPage'));
const MagicLinkSentPage = React.lazy(() => import('@/pages/auth/MagicLinkSentPage'));
const AuthCallbackPage = React.lazy(() => import('@/pages/auth/AuthCallbackPage'));
const MclInvoiceDownloadPage = React.lazy(() => import('@/pages/mcl/MclInvoiceDownloadPage'));
const DriversPortalShell = React.lazy(() => import('@/pages/drivers/DriversPortalShell'));
const OnboardingJourneyPage = React.lazy(() => import('@/pages/drivers/OnboardingJourneyPage'));
const JobsIndexPage = React.lazy(() => import('@/pages/drivers/JobsIndexPage'));
const DepotJobPage = React.lazy(() => import('@/pages/drivers/DepotJobPage'));
const RegionJobsPage = React.lazy(() => import('@/pages/drivers/RegionJobsPage'));
const BlogIndexPage = React.lazy(() => import('@/pages/drivers/BlogIndexPage'));
const BlogArticlePage = React.lazy(() => import('@/pages/drivers/BlogArticlePage'));
const B2BLandingPage = React.lazy(() => import('@/pages/clients/B2BLandingPage'));

// Clients live on partners.parcelink.org. Any /partner hit on another host
// redirects there so old links keep working without duplicate content.
const PARTNERS_URL = 'https://partners.parcelink.org';
const PartnerRedirect = () => {
  React.useEffect(() => { window.location.replace(PARTNERS_URL); }, []);
  return null;
};
const DriverEntry = React.lazy(() => import('@/pages/drivers/DriverEntry'));
const ContractSigningPage = React.lazy(() => import('@/pages/auth/ContractSigningPage'));
const PartnershipSignPage = React.lazy(() => import('@/pages/auth/PartnershipSignPage'));
const OnboardingQuestionsPage = React.lazy(() => import('@/pages/auth/OnboardingQuestionsPage'));
const OnboardingVerifyPage = React.lazy(() => import('@/pages/auth/OnboardingVerifyPage'));
const LogisticsAndBookingPage = React.lazy(() => import('@/pages/auth/LogisticsAndBookingPage'));
const DepotIntakePage = React.lazy(() => import('@/pages/DepotIntakePage'));
const MeetGreetAppointmentPage = React.lazy(() => import('@/pages/auth/MeetGreetAppointmentPage'));
const TeamPage = React.lazy(() => import('@/pages/admin/TeamPage'));
const JoinPortalPage = React.lazy(() => import('@/pages/auth/JoinPortalPage'));
import { ThemeProvider } from '@/contexts/ThemeContext';
import { SubcontractorProfileProvider } from '@/contexts/SubcontractorProfileContext';
import { UnreadMessagesProvider } from '@/contexts/UnreadMessagesContext';
import { AllocationsProvider } from '@/contexts/AllocationsContext';

const SubcontractorRouteLogsPage = React.lazy(() => import('@/pages/subcontractor/SubcontractorRouteLogsPage'));
const LiveInvoicePage = React.lazy(() => import('@/pages/subcontractor/LiveInvoicePage.jsx'));
const ProfilePage = React.lazy(() => import('@/pages/subcontractor/ProfilePage'));
const AvailabilityCalendar = React.lazy(() => import('@/pages/subcontractor/AvailabilityCalendar'));
const SubcontractorRoutingPage = React.lazy(() => import('@/pages/subcontractor/RoutingPage'));
const SubcontractorMessagesPage = React.lazy(() => import('@/pages/subcontractor/MessagesPage'));
const PerformanceHub = React.lazy(() => import('@/pages/subcontractor/PerformanceHub'));
const SubcontractorCasesPage = React.lazy(() => import('@/pages/subcontractor/CasesPage'));
const UniformPage = React.lazy(() => import('@/pages/subcontractor/UniformPage'));
const MyCoachPage = React.lazy(() => import('@/pages/subcontractor/MyCoachPage'));
const ArchivePage = React.lazy(() => import('@/pages/subcontractor/ArchivePage'));
const SubcontractorProtocolsPage = React.lazy(() => import('@/pages/subcontractor/ProtocolsPage'));

const AccountingPortalShell = React.lazy(() => import('@/pages/accounting/AccountingPortalShell'));
const FinancialControlPage = React.lazy(() => import('@/pages/accounting/FinancialControlPage'));
const UnifiedLedgerPage = React.lazy(() => import('@/pages/accounting/UnifiedLedgerPage'));
const ContractorReconciliationPage = React.lazy(() => import('@/pages/accounting/ContractorReconciliationPage'));
const AccountantExportPage = React.lazy(() => import('@/pages/accounting/AccountantExportPage'));
const GrowthPage = React.lazy(() => import('@/pages/accounting/GrowthPage'));
const CrewForecastingPage = React.lazy(() => import('@/pages/accounting/CrewForecastingPage'));
const FreeAgentPage = React.lazy(() => import('@/pages/accounting/FreeAgentPage'));
const AccountantPortalPage = React.lazy(() => import('@/pages/accountant/AccountantPortalPage'));
const AccountingIncomeExpenditurePage = React.lazy(() => import('@/pages/accounting/AccountingIncomeExpenditurePage'));
const DebtResolutionPage = React.lazy(() => import('@/pages/accounting/DebtResolutionPage'));
const CategorisePage = React.lazy(() => import('@/pages/accounting/CategorisePage'));
const PaymentRunPage = React.lazy(() => import('@/pages/accounting/PaymentRunPage'));
const WeekAlignmentPage = React.lazy(() => import('@/pages/accounting/WeekAlignmentPage'));
const AccountantLoginPage = React.lazy(() => import('@/pages/accountant/AccountantLoginPage'));
const PartnerLoginPage = React.lazy(() => import('@/pages/partner-portal/PartnerLoginPage'));
const PartnerPortalLayout = React.lazy(() => import('@/pages/partner-portal/PartnerPortalLayout'));
const PartnerOnboardingSpace = React.lazy(() => import('@/pages/partner-portal/OnboardingSpace'));
const PartnerAgreementSpace = React.lazy(() => import('@/pages/partner-portal/AgreementSpace'));
const PartnerDriversSpace = React.lazy(() => import('@/pages/partner-portal/DriversSpace'));
const PartnerMoneySpace = React.lazy(() => import('@/pages/partner-portal/MoneySpace'));
const PartnerGrowthSpace = React.lazy(() => import('@/pages/partner-portal/GrowthSpace'));
const PartnerMessagesSpace = React.lazy(() => import('@/pages/partner-portal/MessagesSpace'));
const IntroducedPage = React.lazy(() => import('@/pages/partner-portal/IntroducedPage'));

// The holding screen for an address the VPS has not routed yet (see the
// gate at the top of App): its own chunk, so the flow's stylesheet stays
// out of the entry bundle.
const SwitchingOnPage = React.lazy(() => import('@/pages/runmark/SwitchingOnPage'));

function App() {
  const future = {
    v7_startTransition: true,
    v7_relativeSplatPath: true,
  };

  const isDriverSubdomain = React.useMemo(() => {
    const host = window.location.hostname;
    return host.includes('drivers.parcelink.org') ||
      host.includes('drivers-local.parcelink.org') ||
      host.startsWith('drivers.');
  }, []);

  const isAccountingSubdomain = React.useMemo(() => {
    const host = window.location.hostname;
    return host.includes('accounting.parcelink.org') ||
      host.includes('accounting-local.parcelink.org') ||
      host.startsWith('accounting.');
  }, []);

  const isReceiptsSubdomain = React.useMemo(() => {
    const host = window.location.hostname;
    return host.includes('receipts.parcelink.org') ||
      host.includes('receipts-local.parcelink.org') ||
      host.startsWith('receipts.');
  }, []);

  const isPartnersSubdomain = React.useMemo(() => {
    const host = window.location.hostname;
    return host.includes('partners.parcelink.org') ||
      host.includes('partners-local.parcelink.org') ||
      host.startsWith('partners.');
  }, []);

  // runmark.co.uk is the CONTROL PLANE: where other multi-crew operators buy
  // and provision their own portal. It is a different product surface from any
  // tenant portal, so it gets its own shell rather than a route inside one.
  // Tenant hostnames (<tenant>.runmark.co.uk) are deliberately NOT matched here
  // — they serve the portal, and which tenant it is comes from the session.
  const isControlPlane = React.useMemo(() => {
    const host = window.location.hostname;
    return host === 'runmark.co.uk' ||
      host === 'www.runmark.co.uk' ||
      host === 'runmark.localhost';
  }, []);

  // The bare apex (parcelink.org / www) is the public splash that splits traffic
  // to the driver and business journeys. EXACT match so portal./drivers./etc.
  // subdomains are never treated as the apex.
  const isMainDomain = React.useMemo(() => {
    const host = window.location.hostname;
    return host === 'parcelink.org' ||
      host === 'www.parcelink.org' ||
      host === 'parcelink-local.parcelink.org' ||
      host === 'apex.localhost';
  }, []);

  // An address reached over plain http before the VPS has routed it
  // (Traefik's catch-all, deploy/compose-prod.yml): the holding screen and
  // nothing else, no session needed. Any other live host on plain http goes
  // to https, as Traefik's redirect does for the hosts it names.
  const unrouted = React.useMemo(() => unroutedTenantHost(window.location), []);
  const upgrade = React.useMemo(() => (unrouted ? null : httpsUrl(window.location)), [unrouted]);
  React.useEffect(() => { if (upgrade) window.location.replace(upgrade); }, [upgrade]);
  if (unrouted) {
    return (
      <React.Suspense fallback={null}>
        <SwitchingOnPage host={unrouted} />
      </React.Suspense>
    );
  }
  if (upgrade) return null;

  return (
    <ThemeProvider>
      <AppUpdateGate />
      <AuthProvider>
        <OrgProvider>
        <BrandTheme />
        <SubcontractorProfileProvider>
          <UnreadMessagesProvider>
            <AllocationsProvider>
              <BrowserRouter future={future}>
                <PublicType />
                <UpdateOnNavigate />
                <DemoAccessProvider>
                <Analytics />
                <ConsentBanner />
                <WhatsAppChatButton />
                <DemoDeckBar />
                <DemoRouteGuard />
                <AuthStateListener>
                  <RouteErrorBoundary>
                  {/* A prerendered page stays on screen while its chunk loads;
                      anything else gets the spinner (PrerenderedFallback). */}
                  <React.Suspense fallback={<PrerenderedFallback />}>
                  <Routes>
                    {isControlPlane ? (
                      <>
                        <Route path="/" element={<RunmarkLandingPage />} />
                        <Route path="/build" element={<BuildYourVanPage />} />
                        <Route path="/parts" element={<PartsIndexPage />} />
                        <Route path="/parts/:key" element={<PartPage />} />
                        <Route path="/signup" element={<TenantSignupPage />} />
                        <Route path="/welcome" element={<TenantWelcomePage />} />
                        {/* The platform's own console. Runmark manages its
                            tenants here, not inside a tenant's ops portal.
                            The portal's Smart Dash frame, fed by what needs
                            the owner across every tenant. */}
                        <Route path="/control" element={<ConsoleLayout />}>
                          <Route index element={<ConsoleOverviewPage />} />
                          <Route path="tenants" element={<ConsoleTenantsPage />} />
                          <Route path="tenants/:orgKey" element={<ConsoleTenantPage />} />
                          <Route path="domains" element={<ConsoleDomainsPage />} />
                          <Route path="billing" element={<ConsoleBillingPage />} />
                          <Route path="audit" element={<ConsoleAuditPage />} />
                          <Route path="settings" element={<ConsoleSettingsPage />} />
                          <Route path="catalogue" element={<ConsoleCataloguePage />} />
                          <Route path="catalogue/:key/page" element={<ConsolePartCopyPage />} />
                          <Route path="health" element={<ConsoleHealthPage />} />
                          <Route path="access" element={<ConsoleAccessPage />} />
                          <Route path="*" element={<Navigate to="/control" replace />} />
                        </Route>
                        {/* Runmark's own sign-in. Parcelink's AdminLoginPage
                            greeted people with a CREW PORTAL header and
                            "Authorized entry for Parcelink operations" — on
                            runmark.co.uk. Same auth underneath, different
                            product on the outside. */}
                        <Route path="/login" element={<RunmarkLoginPage />} />
                        <Route path="/admin/login" element={<RunmarkLoginPage />} />
                        <Route path="/auth/callback" element={<AuthCallbackPage />} />
                        {/* No /privacy or /terms here on purpose: LegalPage
                            serves PARCELINK's policies, and Runmark publishing
                            its customer's terms as its own is worse than
                            publishing none. Needs Runmark's own before this
                            site takes real money. */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </>
                    ) : isReceiptsSubdomain ? (
                      <>
                        <Route path="/" element={
                          <ProtectedRoute role="admin">
                            <AdminReceiptScannerPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/admin/login" element={<AdminLoginPage />} />
                        <Route path="/auth/callback" element={<AuthCallbackPage />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </>
                    ) : isPartnersSubdomain ? (
                      <>
                        <Route path="/" element={<B2BLandingPage />} />
                        <Route path="/partner" element={<B2BLandingPage />} />
                        <Route path="/blog" element={<BlogIndexPage />} />
                        <Route path="/blog/:slug" element={<BlogArticlePage />} />
                        <Route path="/privacy" element={<LegalPage type="privacy_policy" />} />
                        <Route path="/terms" element={<LegalPage type="terms_of_service" />} />
                        <Route path="/gdpr" element={<LegalPage type="gdpr_statement" />} />
                        <Route path="/disclaimer" element={<LegalPage type="disclaimer" />} />
                        <Route path="*" element={<NotFoundPage />} />
                      </>
                    ) : isDriverSubdomain ? (
                      <>
                        <Route path="/management" element={<ManagementDashboard />} />
                        <Route path="/admin/dashboard" element={<Navigate to="/management" replace />} />
                        <Route path="/privacy" element={<LegalPage type="privacy_policy" />} />
                        <Route path="/terms" element={<LegalPage type="terms_of_service" />} />
                        <Route path="/gdpr" element={<LegalPage type="gdpr_statement" />} />
                        <Route path="/disclaimer" element={<LegalPage type="disclaimer" />} />
                        <Route path="/" element={<DriverEntry />} />
                        <Route path="/subcontractor/login" element={<Navigate to="/" replace />} />
                        <Route path="/onboarding/questions/:candidateId" element={<OnboardingQuestionsPage />} />
                        <Route path="/onboarding/verify/:candidateId" element={<OnboardingVerifyPage />} />
                        <Route path="/onboarding/book/:candidateId" element={<LogisticsAndBookingPage />} />
                        <Route path="/depot/subjects/:token" element={<DepotIntakePage />} />
                        {/* Where cancel/reschedule links land. Opening it must
                            never change anything — scanners follow links. */}
                        <Route path="/appointment/:token" element={<MeetGreetAppointmentPage />} />
                        {/* Admin invitations. Never accepts on load — scanners follow links. */}
                        <Route path="/join/:token" element={<JoinPortalPage />} />
                        <Route path="/journey" element={<OnboardingJourneyPage />} />
                        <Route path="/jobs" element={<JobsIndexPage />} />
                        <Route path="/jobs/region/:regionSlug" element={<RegionJobsPage />} />
                        <Route path="/jobs/:slug" element={<DepotJobPage />} />
                        <Route path="/blog" element={<BlogIndexPage />} />
                        <Route path="/blog/:slug" element={<BlogArticlePage />} />
                        <Route path="/partner" element={<PartnerRedirect />} />
                        <Route path="/drivers/*" element={<DriversPortalShell />} />
                        <Route path="*" element={<NotFoundPage />} />
                      </>
                    ) : isAccountingSubdomain ? (
                      <>
                        <Route path="/" element={<AdminLoginPage />} />
                        <Route path="/subcontractor/login" element={<Navigate to="/" replace />} />
                        <Route path="/auth/callback" element={<AuthCallbackPage />} />

                        <Route
                          element={
                            <ProtectedRoute role="admin">
                              <AccountingPortalShell />
                            </ProtectedRoute>
                          }
                        >
                          <Route index element={<Navigate to="/control" replace />} />
                          <Route path="control" element={<FinancialControlPage />} />
                          <Route path="archive" element={<Navigate to="/contractor-reconciliation" replace />} />
                          <Route path="banking" element={<Navigate to="/unified-ledger" replace />} />
                          <Route path="unified-ledger" element={<UnifiedLedgerPage />} />
                          <Route path="contractor-reconciliation" element={<ContractorReconciliationPage />} />
                          <Route path="accountant-export" element={<AccountantExportPage />} />
                          <Route path="growth" element={<GrowthPage />} />
                          <Route path="crew-forecasting" element={<Navigate to="/crew-forecasting/crews" replace />} />
                          <Route path="crew-forecasting/:tab" element={<CrewForecastingPage />} />
                          <Route path="arena" element={<Navigate to="/crew-forecasting/arena" replace />} />
                          <Route path="freeagent" element={<FreeAgentPage />} />
                          <Route path="ie-report" element={<AccountingIncomeExpenditurePage />} />
                          <Route path="debt-resolution" element={<DebtResolutionPage />} />
                          <Route path="categorise" element={<CategorisePage />} />
                          <Route path="payment-runs" element={<PaymentRunPage />} />
                          <Route path="week-alignment" element={<WeekAlignmentPage />} />
                          <Route path="vat" element={<Navigate to="/accountant-export" replace />} />
                          <Route path="corp-tax" element={<Navigate to="/control" replace />} />
                          {/* Cut pages (council 2026-07-12) — redirect to the cockpit so old links don't 404 */}
                          <Route path="dashboard" element={<Navigate to="/control" replace />} />
                          <Route path="transactions" element={<Navigate to="/control" replace />} />
                          <Route path="self-assessment" element={<Navigate to="/control" replace />} />
                          <Route path="automation" element={<Navigate to="/control" replace />} />
                        </Route>

                        <Route path="*" element={<Navigate to="/" replace />} />
                      </>
                    ) : isMainDomain ? (
                      <>
                        {/* The Aurora-layout home with the road film is the front page (owner,
                            1 Nov: "swap it"). The old first screen is still variant="road" for a
                            one-line rollback; the preview address forwards here. */}
                        <Route path="/" element={<SplashLandingPage variant="aurora" />} />
                        <Route path="/preview/home" element={<Navigate to="/" replace />} />
                        <Route path="/proof" element={<ProofPage />} />
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/software" element={<SoftwarePage />} />
                        <Route path="/blog" element={<BlogIndexPage />} />
                        <Route path="/blog/:slug" element={<BlogArticlePage />} />
                        <Route path="/privacy" element={<LegalPage type="privacy_policy" />} />
                        <Route path="/terms" element={<LegalPage type="terms_of_service" />} />
                        <Route path="/gdpr" element={<LegalPage type="gdpr_statement" />} />
                        <Route path="/disclaimer" element={<LegalPage type="disclaimer" />} />
                        <Route path="*" element={<NotFoundPage />} />
                      </>
                    ) : (
                      <>
                        <Route path="/" element={<SubcontractorLoginPage />} />
                        {/* Ten places in the app send people here (logout, a failed
                            callback, ProtectedRoute, the magic-link pages). It was
                            never routed, so every one of them landed on the 404. */}
                        <Route path="/subcontractor/login" element={<SubcontractorLoginPage />} />
                        <Route path="/demo" element={<DemoEntryPage />} />

                        <Route path="/admin/login" element={<AdminLoginPage />} />
                        <Route path="/magic-link-confirmation" element={<MagicLinkConfirmationPage />} />
                        <Route path="/magic-link-sent" element={<MagicLinkSentPage />} />
                        <Route path="/auth/callback" element={<AuthCallbackPage />} />

                        <Route
                          path="/subcontractor/dashboard"
                          element={
                            <ProtectedRoute role="subcontractor">
                              <AgreementGate>
                                <MessageGate>
                                  <ArchiveGate>
                                    <SubcontractorDashboardPage />
                                  </ArchiveGate>
                                </MessageGate>
                              </AgreementGate>
                            </ProtectedRoute>
                          }
                        >
                          <Route index element={null} />
                          {/* Each crew page is a part's surface (src/lib/partSurfaces.js):
                              the gate is a no-op for the flagship and bites a
                              tenant whose operator has not bought the part. */}
                          <Route path="availability" element={<ModuleGate module="operations"><AvailabilityCalendar /></ModuleGate>} />
                          <Route path="routing" element={<ModuleGate module="routes-pay"><SubcontractorRoutingPage /></ModuleGate>} />
                          <Route path="logs" element={<ModuleGate module="routes-pay"><SubcontractorRouteLogsPage /></ModuleGate>} />
                          <Route path="live-invoice" element={<ModuleGate module="invoicing"><LiveInvoicePage /></ModuleGate>} />
                          <Route path="messages" element={<SubcontractorMessagesPage />} />
                          <Route path="performance" element={<ModuleGate module="performance"><PerformanceHub /></ModuleGate>} />
                          <Route path="cases" element={<ModuleGate module="field-cases"><SubcontractorCasesPage /></ModuleGate>} />
                          <Route path="uniform" element={<ModuleGate module="operations"><UniformPage /></ModuleGate>} />
                          <Route path="archive" element={<ArchivePage />} />
                          <Route path="my-coach" element={<MyCoachPage />} />
                          {/* My Day — gated on the 'my-day' module entitlement.
                              No-op for Parcelink (fails safe ON); a second
                              tenant without the module sees the gate. The old
                              /protocols URL lives on in pushes and bookmarks —
                              redirect, never break it. */}
                          <Route path="my-day" element={<ModuleGate module="my-day"><SubcontractorProtocolsPage /></ModuleGate>} />
                          <Route path="protocols" element={<Navigate to="/subcontractor/dashboard/my-day" replace />} />
                          <Route path="profile" element={<ProfilePage />} />
                          {FIELD_ASSIST_ENABLED && <Route path="field-assist" element={<ModuleGate module="field-assist"><FieldAssistPage /></ModuleGate>} />}
                          <Route path="route-log-form" element={<Navigate to="/subcontractor/dashboard/logs" replace />} />
                          <Route path="route-logs" element={<Navigate to="/subcontractor/dashboard/logs" replace />} />
                          <Route path="*" element={<Navigate to="/subcontractor/dashboard" replace />} />
                        </Route>


                        {/* Chromeless stage the ROOM sees — dragged onto the
                            projector while the admin window stays private. */}
                        <Route
                          path="/admin/projector"
                          element={
                            <ProtectedRoute role="admin">
                              <ProjectorStagePage />
                            </ProtectedRoute>
                          }
                        />

                        <Route path="/admin/dashboard" element={<AdminDashboardShell />}>
                          <Route index element={<AdminDashboardPage />} />
                          <Route path="depots" element={<AdminDepotsPage />} />
                          <Route path="setup" element={<AdminSetupPage />} />
                          <Route path="setup/crew-kit" element={<AdminCrewKitPage />} />
                          <Route path="setup/brand" element={<AdminBrandingPage />} />
                          <Route path="platform" element={<AdminPlatformPage />} />
                          <Route path="plan" element={<AdminPartnerPlanPage />} />
                          <Route path="setup/sending" element={<AdminSendingIdentityPage />} />
                          <Route path="setup/domain" element={<AdminWebAddressPage />} />
                          <Route path="crews" element={<AdminCrewsPage />} />
                          <Route path="subcontractors" element={<AdminSubcontractorsPage />} />
                          <Route path="calendars" element={<AdminCalendarsPage />} />
                          <Route path="routing" element={<AdminRoutingPage />} />
                          <Route path="logging" element={<AdminLoggingPage />} />
                          <Route path="activity" element={<ActivityPage />} />
                          <Route path="sync-logs" element={<AdminSyncLogsPage />} />
                          <Route path="invoicing" element={<AdminInvoicingPage />} />
                          <Route path="time-requests" element={<TimeRequestsPage />} />
                          <Route path="uniforms" element={<AdminUniformsPage />} />
                          <Route path="terminations" element={<AdminTerminationsPage />} />
                          <Route path="additional-work" element={<AdditionalWorkPage />} />
                          <Route path="availability-cancellations" element={<AvailabilityCancellationsPage />} />
                          <Route path="financials" element={<FinancialsPage />} />
                          <Route path="accounting" element={<AccountingPage />} />
                          <Route path="accounting/settings" element={<AccountingSettingsPage />} />
                          <Route path="accounting/director-remuneration" element={<DirectorRemunerationPage />} />
                          <Route path="accounting/uncategorized" element={<UncategorizedTransactionsPage />} />
                          <Route path="payment-schedules" element={<PaymentSchedulesPage />} />
                          <Route path="partnerships" element={<PartnershipsPage />} />
                          <Route path="pay-run" element={<PayRunPage />} />
                          <Route path="invoice-settings" element={<InvoiceSettingsPage />} />
                          <Route path="webhooks" element={<AdminWebhooksPage />} />
                          <Route path="messages" element={<AdminMessagesPage />} />
                          <Route path="mail" element={<AdminMailPage />} />
                          <Route path="notifications" element={<AdminNotificationsPage />} />
                          <Route path="allocations" element={<AdminAllocationsPage />} />
                          <Route path="settings" element={<AdminSettingsPage />} />
                          <Route path="team" element={<TeamPage />} />
                          <Route path="analytics" element={<AdminAnalyticsPage />} />
                          <Route path="ai-insights" element={<AIInsightsPage />} />
                          {FIELD_ASSIST_ENABLED && <Route path="field-intelligence" element={<FieldIntelligenceReviewDashboard />} />}
                          {FIELD_ASSIST_ENABLED && <Route path="research-programme" element={<ResearchProgrammePage />} />}
                          <Route path="performance" element={<AdminPerformanceHub />} />
                          <Route path="performance/trends" element={<CrewTrendsPage />} />
                          <Route path="performance/network" element={<NetworkScoreboardPage />} />
                          <Route path="reports" element={<ReportsArchivePage />} />
                          {/* Recruitment is ONE page of tabs. The old routes stay as
                              redirects so every existing link still lands on the
                              right tab rather than a second copy of the screen. */}
                          <Route path="recruitment" element={<RecruitmentPage />} />
                          <Route path="recruitment/onboarding" element={<RecruitmentRedirect tab="settings" section="brain" />} />
                          <Route path="recruitment/rescue" element={<RecruitmentRedirect tab="rescue" section="whatsapp" />} />
                          <Route path="marketing" element={<MarketingPage />} />
                          <Route path="engine-room" element={<EngineRoomPage />} />
                          <Route path="platform-health" element={<PlatformHealthPage />} />
                          <Route path="recruitment/training" element={<RecruitmentRedirect tab="training" section="planner" />} />
                          <Route path="jobs" element={<JobArchivePage />} />
                          <Route path="national-insights" element={<NationalInsightsPage />} />
                          <Route path="data-tools" element={<DataToolsPage />} />
                          <Route path="product-demos" element={<ProductDemosPage />} />
                          <Route path="decks" element={<DecksPage />} />
                          <Route path="business-van-lab" element={<BusinessVanLabPage />} />
                          <Route path="verifications" element={<RecruitmentRedirect tab="verification" section="sessions" />} />
                          <Route path="agreements" element={<AdminAgreementsPage />} />
                          <Route path="field-cases" element={<AdminFieldCasesPage />} />
                          <Route path="protocols" element={<AdminProtocolsPage />} />
                          <Route path="inspect/:id" element={<SubcontractorInspector />} />
                        </Route>

                        {/* Accounting Hub — accessible from the main portal without re-login */}
                        <Route
                          path="/accounting-hub"
                          element={
                            <ProtectedRoute role="admin">
                              <AccountingPortalShell />
                            </ProtectedRoute>
                          }
                        >
                          <Route index element={<Navigate to="/accounting-hub/control" replace />} />
                          <Route path="control" element={<FinancialControlPage />} />
                          <Route path="archive" element={<Navigate to="/accounting-hub/contractor-reconciliation" replace />} />
                          <Route path="banking" element={<Navigate to="/accounting-hub/unified-ledger" replace />} />
                          <Route path="unified-ledger" element={<UnifiedLedgerPage />} />
                          <Route path="contractor-reconciliation" element={<ContractorReconciliationPage />} />
                          <Route path="accountant-export" element={<AccountantExportPage />} />
                          <Route path="growth" element={<GrowthPage />} />
                          <Route path="crew-forecasting" element={<Navigate to="/accounting-hub/crew-forecasting/crews" replace />} />
                          <Route path="crew-forecasting/:tab" element={<CrewForecastingPage />} />
                          <Route path="arena" element={<Navigate to="/accounting-hub/crew-forecasting/arena" replace />} />
                          <Route path="freeagent" element={<FreeAgentPage />} />
                          <Route path="ie-report" element={<AccountingIncomeExpenditurePage />} />
                          <Route path="debt-resolution" element={<DebtResolutionPage />} />
                          <Route path="categorise" element={<CategorisePage />} />
                          <Route path="payment-runs" element={<PaymentRunPage />} />
                          <Route path="week-alignment" element={<WeekAlignmentPage />} />
                          <Route path="vat" element={<Navigate to="/accounting-hub/accountant-export" replace />} />
                          <Route path="corp-tax" element={<Navigate to="/accounting-hub/control" replace />} />
                          {/* Cut pages (council 2026-07-12) — redirect to the cockpit so old links don't 404 */}
                          <Route path="dashboard" element={<Navigate to="/accounting-hub/control" replace />} />
                          <Route path="transactions" element={<Navigate to="/accounting-hub/control" replace />} />
                          <Route path="self-assessment" element={<Navigate to="/accounting-hub/control" replace />} />
                          <Route path="automation" element={<Navigate to="/accounting-hub/control" replace />} />
                        </Route>

                        {/* Standalone read-only accountant portal */}
                        <Route path="/accountant/login" element={<AccountantLoginPage />} />
                        <Route path="/accountant" element={<ProtectedRoute role="accountant"><AccountantPortalPage /></ProtectedRoute>} />

                        {/* Crew Partnership: the partner portal (stage 4) and an introduced driver's link (4b). */}
                        <Route path="/partner-portal/login" element={<PartnerLoginPage />} />
                        <Route path="/partner-portal" element={<ProtectedRoute role="partner"><PartnerPortalLayout /></ProtectedRoute>}>
                          <Route index element={<PartnerOnboardingSpace />} />
                          <Route path="agreement" element={<PartnerAgreementSpace />} />
                          <Route path="drivers" element={<PartnerDriversSpace />} />
                          <Route path="money" element={<PartnerMoneySpace />} />
                          <Route path="growth" element={<PartnerGrowthSpace />} />
                          <Route path="messages" element={<PartnerMessagesSpace />} />
                          <Route path="*" element={<Navigate to="/partner-portal" replace />} />
                        </Route>
                        <Route path="/introduced/:token" element={<IntroducedPage />} />

                        <Route path="/mcl/invoice/:token" element={<MclInvoiceDownloadPage />} />
                        <Route path="/sign/:token" element={<ContractSigningPage />} />
                        {/* Partner Hub slice 5: a presentation deck, followed by code on any device. */}
                        <Route path="/deck/:code" element={<DeckViewerPage />} />
                        {/* Crew Partnership: the partner's signatory signs by emailed link (stage 3). */}
                        <Route path="/partnership-sign/:token" element={<PartnershipSignPage />} />
                        <Route path="/onboarding/questions/:candidateId" element={<OnboardingQuestionsPage />} />
                        <Route path="/onboarding/verify/:candidateId" element={<OnboardingVerifyPage />} />
                        <Route path="/onboarding/book/:candidateId" element={<LogisticsAndBookingPage />} />
                        <Route path="/depot/subjects/:token" element={<DepotIntakePage />} />
                        {/* Where cancel/reschedule links land. Opening it must
                            never change anything — scanners follow links. */}
                        <Route path="/appointment/:token" element={<MeetGreetAppointmentPage />} />
                        {/* Admin invitations. Never accepts on load — scanners follow links. */}
                        <Route path="/join/:token" element={<JoinPortalPage />} />
                        <Route path="/journey" element={<OnboardingJourneyPage />} />
                        <Route path="/jobs" element={<JobsIndexPage />} />
                        <Route path="/jobs/region/:regionSlug" element={<RegionJobsPage />} />
                        <Route path="/jobs/:slug" element={<DepotJobPage />} />
                        <Route path="/blog" element={<BlogIndexPage />} />
                        <Route path="/blog/:slug" element={<BlogArticlePage />} />
                        <Route path="/partner" element={<PartnerRedirect />} />
                        <Route path="/drivers/*" element={<DriversPortalShell />} />
                        {/* An unknown path is not the homepage. Redirecting here
                            served the full marketing page, with its canonical and
                            its content, under every made-up URL a crawler tried —
                            unbounded duplicate content, all returning 200.
                            NotFoundPage carries noindex; nginx supplies the 404. */}
                        <Route path="*" element={<NotFoundPage />} />
                      </>
                    )}
                  </Routes>
                  </React.Suspense>
                  </RouteErrorBoundary>
                </AuthStateListener>
                </DemoAccessProvider>
              </BrowserRouter>
            </AllocationsProvider>
          </UnreadMessagesProvider>
        </SubcontractorProfileProvider>
        </OrgProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;